/**
 * R-IMAP-GATEWAY-EDIT (2026-09-19) — data layer for the "IMAP Proxy (SOCKS5)"
 * card on the web.de and t-online.de admin pages.
 *
 * Both providers read IMAP through a rotating residential SOCKS5 gateway. The
 * operator can now save that gateway from the page; the mail runtime treats the
 * saved row as authoritative (the K8s Secret value is only the fallback while no
 * row exists) and picks it up within ~1 minute.
 *
 *   GET /admin/providers/:providerId/imap-gateway   (provider:inspect)
 *       → { providerId, configured:false }
 *       | { providerId, configured:true, host, port, usernameMasked, updatedAt, updatedBy }
 *   PUT /admin/providers/:providerId/imap-gateway   (provider:manage_inventory)
 *       body { gateway: "host:port:user:pass" }
 *       → 200 { outcome:"updated", providerId, host, port, usernameMasked, updatedAt }
 *       → 400 { error:"invalid_gateway", reason: format|host|port|user|pass|too_long }
 *       → 503 { error:"credential_vault_not_configured" }
 *       → 404 { error:"provider_not_supported" }
 *
 * The server never returns the password or the full proxy username, and this
 * module never echoes the submitted value back into any message.
 */
import { callAdminApi, type ApiClientOptions, type ApiResult } from '../../api/client.js';

/** Only these two providers have an operator-editable IMAP gateway. */
export type ImapGatewayProviderId = 'webde' | 'tonline';

export type ImapGatewayStatus =
  | { readonly providerId: string; readonly configured: false }
  | {
      readonly providerId: string;
      readonly configured: true;
      readonly host: string;
      readonly port: number;
      readonly usernameMasked: string;
      readonly updatedAt: string;
      readonly updatedBy: string | null;
    };

export interface ImapGatewayUpdateResponse {
  readonly outcome: 'updated';
  readonly providerId: string;
  readonly host: string;
  readonly port: number;
  readonly usernameMasked: string;
  readonly updatedAt: string;
}

export interface ImapGatewayUpdateInput {
  readonly providerId: ImapGatewayProviderId;
  readonly gateway: string;
}

function gatewayPath(providerId: ImapGatewayProviderId): string {
  return `/admin/providers/${encodeURIComponent(providerId)}/imap-gateway`;
}

/**
 * The shared client only lifts `body.reason` into `reason`; bodies that carry
 * just `{ error }` (503 credential_vault_not_configured, 404
 * provider_not_supported) would surface as the opaque `http_<status>`. Lift the
 * server's `error` code in that case so the UI can map it to Turkish text.
 */
function liftErrorCode<T>(res: ApiResult<T>): ApiResult<T> {
  if (res.kind !== 'error' || !res.reason.startsWith('http_')) return res;
  const code = res.body?.error;
  return typeof code === 'string' && code.length > 0 ? { ...res, reason: code } : res;
}

export async function fetchImapGateway(
  options: ApiClientOptions,
  providerId: ImapGatewayProviderId,
): Promise<ApiResult<ImapGatewayStatus>> {
  return liftErrorCode(
    await callAdminApi<ImapGatewayStatus>(options, { path: gatewayPath(providerId) }),
  );
}

export async function updateImapGateway(
  options: ApiClientOptions,
  input: ImapGatewayUpdateInput,
): Promise<ApiResult<ImapGatewayUpdateResponse>> {
  return liftErrorCode(
    await callAdminApi<ImapGatewayUpdateResponse>(options, {
      method: 'PUT',
      path: gatewayPath(input.providerId),
      body: { gateway: input.gateway },
    }),
  );
}

// ---------------------------------------------------------------------------
// Client-side validation — mirrors the API rules exactly (the server remains
// authoritative; this only saves a round-trip and gives a precise message).
// ---------------------------------------------------------------------------

export type GatewayInvalidReason = 'format' | 'host' | 'port' | 'user' | 'pass' | 'too_long';

export const GATEWAY_MAX_LENGTH = 512;
const HOST_RE = /^[A-Za-z0-9.-]{1,253}$/;
const PORT_RE = /^[0-9]{1,5}$/;
const WHITESPACE_RE = /\s/;

export type GatewayValidation =
  | { readonly ok: true; readonly value: string }
  | { readonly ok: false; readonly reason: GatewayInvalidReason };

/**
 * Validate "host:port:user:pass". Surrounding whitespace (a pasted trailing
 * newline) is trimmed first; everything else follows the API: exactly 4
 * ":"-separated parts, host = hostname/IPv4 chars (1..253), port 1..65535,
 * user and pass non-empty without ":" or whitespace, total length <= 512.
 */
export function validateGateway(raw: string): GatewayValidation {
  const value = raw.trim();
  if (value.length > GATEWAY_MAX_LENGTH) return { ok: false, reason: 'too_long' };
  const parts = value.split(':');
  if (parts.length !== 4) return { ok: false, reason: 'format' };
  const [host, port, user, pass] = parts as [string, string, string, string];
  if (!HOST_RE.test(host)) return { ok: false, reason: 'host' };
  const portNum = PORT_RE.test(port) ? Number(port) : Number.NaN;
  if (!Number.isInteger(portNum) || portNum < 1 || portNum > 65535) return { ok: false, reason: 'port' };
  if (user.length === 0 || WHITESPACE_RE.test(user)) return { ok: false, reason: 'user' };
  if (pass.length === 0 || WHITESPACE_RE.test(pass)) return { ok: false, reason: 'pass' };
  return { ok: true, value };
}

const REASON_TEXT: Readonly<Record<string, string>> = {
  format: 'Biçim hatalı: değer tam 4 parçadan oluşmalı — host:port:kullanıcı:şifre.',
  host: 'Host geçersiz: yalnız harf, rakam, nokta ve tire içerebilir (en fazla 253 karakter).',
  port: 'Port geçersiz: 1 ile 65535 arasında bir sayı olmalı.',
  user: 'Kullanıcı adı geçersiz: boş olamaz, ":" veya boşluk içeremez.',
  pass: 'Şifre geçersiz: boş olamaz, ":" veya boşluk içeremez.',
  too_long: 'Değer çok uzun: en fazla 512 karakter olabilir.',
  invalid_gateway: 'Proxy değeri geçersiz.',
  credential_vault_not_configured:
    'Sunucuda kimlik kasası yapılandırılmamış — proxy şifresi saklanamadığı için kaydedilmedi.',
  provider_not_supported: 'Bu sağlayıcı için IMAP proxy ayarı desteklenmiyor.',
  forbidden: 'Yetki yok: bu işlem için gerekli izin hesabınızda yok.',
  auth_required: 'Oturum gerekli — lütfen admin girişi yapın.',
  empty_response_body: 'Sunucu boş yanıt döndü.',
  imap_gateway_not_migrated: 'Veritabanı tablosu henüz oluşturulmamış — proxy kaydedilemedi. Teknik ekibe bildirin.',
  imap_gateway_save_failed: 'Proxy kaydedilemedi (sunucu hatası). Birkaç saniye sonra tekrar deneyin.',
};

/**
 * Turkish operator text for an API / validation reason code. Only the code is
 * ever shown — never the submitted value (it contains the proxy password).
 */
export function gatewayReasonText(reason: string): string {
  const known = REASON_TEXT[reason];
  if (known) return known;
  if (reason.startsWith('network:')) return 'Bağlantı hatası — sunucuya ulaşılamadı.';
  return `İstek başarısız (${reason}).`;
}
